import _ from 'lodash';
import { isMobileApp } from './getHost';

/* -------------------------------------------------------------------------- */
/*                                Local storage                               */
/* -------------------------------------------------------------------------- */

import { getAuthenticatorForRegion } from "../api/managers/servers";

/* ---------------------------------- Keys ---------------------------------- */

export const LOCAL_STORAGE_LAST_LIVE_LAYOUT = 'lastSelectedLiveFeedsLayout';
export const LOCAL_STORAGE_COLLAPSED_SIDEBAR = 'collapsedSidebar';
export const LOCAL_STORAGE_EVENTS_FILTERS = 'eventsFilters';
export const LOCAL_STORAGE_BANNER_CLOSED = 'bannerClosed';
export const LOCAL_STORAGE_SHOW_CAMERA_NAMES = 'showCamNames';
export const LOCAL_STORAGE_LIVE_STILLS_MODE = 'liveStillsMode';
export const LOCAL_STORAGE_LIVE_STILLS_BANNER_CLOSED = 'liveStillsModeBannerClosed';
export const LOCAL_STORAGE_CAMERA_NAVIGATOR_EXPANDED = 'cameraNavigatorExpanded';
export const LOCAL_STORAGE_CAMERA_NAVIGATOR_SELECTION = 'cameraNavigatorSelection';
export const LOCAL_STORAGE_LIVE_GRID_FILTERS = 'gridViewFilters';
export const LOCAL_STORAGE_ALERTS_FILTERS = 'alertsFilters';
export const LOCAL_STORAGE_VEHICLES_FILTERS = 'vehiclesFilters';
export const LOCAL_STORAGE_PEOPLE_COUNTING_FILTERS = 'peopleCountingFilters';
export const LOCAL_STORAGE_PPE_FILTERS = 'ppeFilters';
export const LOCAL_STORAGE_PLAYBACK_SELECTION = 'playbackSelection';
export const LOCAL_STORAGE_SETTINGS_SELECTION = 'settingsSelection';
export const LOCAL_STORAGE_PLAYBACK_TIME = 'playbackTime';
export const LOCAL_STORAGE_PLAYBACK_VISIBLE_RANGE = 'playbackTimelineVisibleRange';
export const LOCAL_STORAGE_SHOW_INSTALLER_WARNING = 'showInstallerWarning';
export const LOCAL_STORAGE_ORDERCAM_SELECTED_ORGS = 'ordercamSelectedOrgs';
export const LOCAL_STORAGE_MOBILE_APP_CAMERAS_GRID_COLUMNS = 'mobileAppCamerasGridColumns';
export const LOCAL_STORAGE_MOBILE_APP_PLATFORM = 'platform'; // 'ios' or 'android'
export const LOCAL_STORAGE_MOBILE_APP_PUSH_TOKEN = 'pushToken';
export const LOCAL_STORAGE_MOBILE_APP_PHONE_ID = 'phoneId';
export const LOCAL_STORAGE_MOBILE_APP_PHONE_NAME = 'phoneName';
export const LOCAL_STORAGE_MOBILE_APP_PHONE_MODEL = 'phoneModel';
export const LOCAL_STORAGE_MOBILE_APP_VERSION = 'appVersion';
export const LOCAL_STORAGE_GRID_CAMERA_ORDER = 'gridCameraOrder';
export const LOCAL_STORAGE_OBJECTS_GRID_LAYOUT = 'objectsGridLayout';
export const LOCAL_STORAGE_PLAN_CANCELLED_BANNER_DISMISSED = 'planCancelledBannerDismissed';
export const LOCAL_STORAGE_FEATURE_BANNER_DISMISSED = 'featureBannerDismissed';

/* --------------------------------- Helpers -------------------------------- */

const getPageFilterForLoggedInUser = localStorageKey => (loggedInUserUid, filter) => {
    if (localStorage[localStorageKey]) {
        try {
            return JSON.parse(localStorage[localStorageKey])?.[loggedInUserUid]?.[filter];
        } catch (error) {
            console.error(error);
        }
    }
    return undefined;
}

const setPageFilterForLoggedInUser = localStorageKey => (loggedInUserUid, filter, value) => {
    let filters = {};
    if (localStorage[localStorageKey]) {
        try {
            filters = JSON.parse(localStorage[localStorageKey]);
        } catch (error) {
            console.error(`Unable to parse ${localStorageKey} in local storage so overwriting. Error: ${error}`);
        }
    }
    if (!filters[loggedInUserUid]) {
        filters[loggedInUserUid] = {};
    }
    filters[loggedInUserUid][filter] = value;
    localStorage[localStorageKey] = JSON.stringify(filters);
};

const clearPageFiltersForLoggedInUser = localStorageKey => (loggedInUserUid) => {
    let filters = {};
    if (localStorage[localStorageKey]) {
        try {
            filters = JSON.parse(localStorage[localStorageKey]);
        } catch (error) {
            console.error(`Unable to parse ${localStorageKey} in local storage so overwriting. Error: ${error}`);
        }
    }
    delete filters[loggedInUserUid];
    localStorage[localStorageKey] = JSON.stringify(filters);
};

const getPageScopedFilterForLoggedInUser = localStorageKey => (loggedInUserUid, page, filter) => {
    if (localStorage[localStorageKey]) {
        try {
            return JSON.parse(localStorage[localStorageKey])?.[loggedInUserUid]?.[page]?.[filter];
        } catch (error) {
            console.error(error);
        }
    }
    return undefined;
};

const setPageScopedFilterForLoggedInUser = localStorageKey => (loggedInUserUid, page, filter, value) => {
    let filters = {};
    if (localStorage[localStorageKey]) {
        try {
            filters = JSON.parse(localStorage[localStorageKey]);
        } catch (error) {
            console.error(`Unable to parse ${localStorageKey} in local storage so overwriting. Error: ${error}`);
        }
    }
    if (!filters[loggedInUserUid]) {
        filters[loggedInUserUid] = {};
    }
    if (!filters[loggedInUserUid][page]) {
        filters[loggedInUserUid][page] = {};
    }
    filters[loggedInUserUid][page][filter] = value;
    localStorage[localStorageKey] = JSON.stringify(filters);
};

const clearPageScopedFiltersForLoggedInUser = localStorageKey => (loggedInUserUid, page) => {
    let filters = {};
    if (localStorage[localStorageKey]) {
        try {
            filters = JSON.parse(localStorage[localStorageKey]);
        } catch (error) {
            console.error(`Unable to parse ${localStorageKey} in local storage so overwriting. Error: ${error}`);
        }
    }

    if (filters[loggedInUserUid]) {
        delete filters[loggedInUserUid][page];

        if (Object.keys(filters[loggedInUserUid]).length === 0) {
            delete filters[loggedInUserUid];
        }
    }

    localStorage[localStorageKey] = JSON.stringify(filters);
};



// Once all pages are in web app, we shouldn't need to clear local storage as it should only be used for values that need to persist between logins
export const clearLocalStorage = async () => {
    // Clear login data from native layer
    if (window?.webkit?.messageHandlers?.nativeStorage) {
        await window.webkit.messageHandlers.nativeStorage.postMessage({
            action: 'clearWebLogin'
        });
    }

    // Local storage items that must not be removed
    const keysToKeep = [
        LOCAL_STORAGE_LAST_LIVE_LAYOUT,
        LOCAL_STORAGE_COLLAPSED_SIDEBAR,
        LOCAL_STORAGE_EVENTS_FILTERS,
        LOCAL_STORAGE_BANNER_CLOSED,
        LOCAL_STORAGE_SHOW_CAMERA_NAMES,
        LOCAL_STORAGE_LIVE_STILLS_MODE,
        LOCAL_STORAGE_LIVE_STILLS_BANNER_CLOSED,
        LOCAL_STORAGE_CAMERA_NAVIGATOR_SELECTION,
        LOCAL_STORAGE_LIVE_GRID_FILTERS,
        LOCAL_STORAGE_ALERTS_FILTERS,
        LOCAL_STORAGE_CAMERA_NAVIGATOR_EXPANDED,
        LOCAL_STORAGE_PLAYBACK_SELECTION,
        LOCAL_STORAGE_SETTINGS_SELECTION,
        LOCAL_STORAGE_PLAYBACK_TIME,
        LOCAL_STORAGE_PLAYBACK_VISIBLE_RANGE,
        LOCAL_STORAGE_VEHICLES_FILTERS,
        LOCAL_STORAGE_PEOPLE_COUNTING_FILTERS,
        LOCAL_STORAGE_SHOW_INSTALLER_WARNING,
        LOCAL_STORAGE_ORDERCAM_SELECTED_ORGS,
        LOCAL_STORAGE_MOBILE_APP_PHONE_ID,
        LOCAL_STORAGE_MOBILE_APP_PUSH_TOKEN,
        LOCAL_STORAGE_MOBILE_APP_PLATFORM,
        LOCAL_STORAGE_MOBILE_APP_PHONE_NAME,
        LOCAL_STORAGE_MOBILE_APP_PHONE_MODEL,
        LOCAL_STORAGE_MOBILE_APP_VERSION,
        LOCAL_STORAGE_GRID_CAMERA_ORDER,
        LOCAL_STORAGE_OBJECTS_GRID_LAYOUT,
        LOCAL_STORAGE_PLAN_CANCELLED_BANNER_DISMISSED,
        LOCAL_STORAGE_FEATURE_BANNER_DISMISSED
    ];

    // Get items before clearing storage so we can put them back after
    const itemsToKeep = keysToKeep.map((key) => ({
        key,
        data: localStorage.getItem(key),
    }));

    localStorage.clear();

    // Put data back that we want to keep
    itemsToKeep.forEach(({ key, data }) => {
        if (data) {
            localStorage.setItem(key, data);
        }
    });
};

// data is the object returned from successful login request
// webLogin, loginResponse, authenticator and portalusername get put in local storage
// Use this for email/password logins
export const updateLocalStorageOnLogin = async (data) => {
    // Replace localStorage.webLogin completely
    if (window?.webkit?.messageHandlers?.nativeStorage) {
        await window.webkit.messageHandlers.nativeStorage.postMessage({
            action: 'putWebLogin',
            value: JSON.stringify(data.webLogin)
        });
    } else {
        localStorage.webLogin = JSON.stringify(data.webLogin);
    }

    // Add loginResponse to local storage
    // Must not store authToken or managementToken
    const dataWithoutTokens = { ...data };
    delete dataWithoutTokens.authToken;
    delete dataWithoutTokens.managementToken;
    localStorage.loginResponse = JSON.stringify(dataWithoutTokens);

    // Add authenticator to local storage
    // This can be ditched when other web repos have been dropped
    localStorage.authenticator = getAuthenticatorForRegion(data.region);

    // Add portalusername to local storage
    // Portal gets user email from this (Why? No idea!)
    localStorage.portalusername = data.email;

    localStorage.installerId = data.installerid;
    localStorage.distributorId = data.distributorid;
};

export const updateNativeLayerOnRefreshToken = async (data) => {
    if (!window?.webkit?.messageHandlers?.nativeStorage) {
        updateLocalStorageOnRefreshToken(data);
        return;
    }

    return window.webkit.messageHandlers.nativeStorage.postMessage({
        action: 'putWebLogin',
        value: JSON.stringify(data.webLogin)
    });
};

// data is the object returned from successful login request
// loginToken gets updated in localStorage.webLogin and localStorage.loginResponse
export const updateLocalStorageOnRefreshToken = data => {
    // Just update webLogin (with new loginToken)
    let previousWebLogin = {};
    if (localStorage.webLogin) {
        try {
            previousWebLogin = JSON.parse(localStorage.webLogin);
        } catch {}
    }

    if (previousWebLogin?.updated && data?.webLogin?.updated && previousWebLogin.updated >= data.webLogin.updated) {
        // Don't update if existing data is newer than what we've got back from server
        // Prevents timing issue when multiple requests are made in quick succession
        return;
    }

    const webLogin = { ...previousWebLogin, ...data.webLogin };
    localStorage.webLogin = JSON.stringify(webLogin);

    // loginResponse also contains webLogin
    if (localStorage.loginResponse) {
        try {
            localStorage.loginResponse = JSON.stringify({ ...JSON.parse(localStorage.loginResponse), webLogin });
        } catch {}
    }
}

// Get seriesId, uid, token, and authenticator (if possible) from local storage
export const getLoginDetailsFromLocalStorage = () => {
    // Try to parse webLogin
    const details = JSON.parse(localStorage.webLogin);

    // Try to get authenticator from local storage
    if (localStorage.loginResponse) {
        try {
            details.authenticator = JSON.parse(
                localStorage.loginResponse
            ).authenticator;
        } catch (error) {}
    }

    return details;
};

// Camera navigator selection stored per page for each logged in uid
export const getCameraNavigatorSelectionForPageForLoggedInUser = (loggedInUserUid, page) => {
    if (localStorage[LOCAL_STORAGE_CAMERA_NAVIGATOR_SELECTION]) {
        try {
            return JSON.parse(localStorage[LOCAL_STORAGE_CAMERA_NAVIGATOR_SELECTION])?.[loggedInUserUid]?.[page];
        } catch (error) {
            console.error(error);
        }
    }
    return undefined;
};

/**
 * Update the stored camera navigator selection for a page.
 * @param {number} loggedInUserUid Logged in user's uid.
 * @param {string} page Page identifier to store selection against.
 * @param {string[]|null} selectedUidds New selection. To clear selection, this should be empty array or null.
 */
export const setCameraNavigatorSelectionForPageForLoggedInUser = (loggedInUserUid, page, selectedUidds) => {
    let gridViewCameraSelection = {};
    if (localStorage[LOCAL_STORAGE_CAMERA_NAVIGATOR_SELECTION]) {
        try {
            gridViewCameraSelection = JSON.parse(localStorage[LOCAL_STORAGE_CAMERA_NAVIGATOR_SELECTION]);
        } catch (error) {
            console.error(`Unable to parse ${LOCAL_STORAGE_CAMERA_NAVIGATOR_SELECTION} in local storage so overwriting. Error: ${error}`);
        }
    }

    if (!selectedUidds || selectedUidds.length === 0) {
        // Clear selection
        if (gridViewCameraSelection[loggedInUserUid]) {
            delete gridViewCameraSelection[loggedInUserUid][page];

            if (_.isEmpty(gridViewCameraSelection[loggedInUserUid])) {
                delete gridViewCameraSelection[loggedInUserUid];
            }
        }
    } else {
        // Update selection
        if (!gridViewCameraSelection[loggedInUserUid]) {
            gridViewCameraSelection[loggedInUserUid] = {};
        }

        gridViewCameraSelection[loggedInUserUid][page] = selectedUidds;
    }
    
    if (_.isEmpty(gridViewCameraSelection)) {
        // Nothing left to store
        localStorage.removeItem(LOCAL_STORAGE_CAMERA_NAVIGATOR_SELECTION);
    } else {
        // Update value
        localStorage[LOCAL_STORAGE_CAMERA_NAVIGATOR_SELECTION] = JSON.stringify(gridViewCameraSelection);
    }
};

/**
 * Get stored filter values for live grid view.
 * @param {number} loggedInUserUid Uid of logged in user.
 * @param {('status'|'tags')} filter Id of filter.
 * @returns {*} Value of specified filter for specified user. If no value is stored, undefined will be returned;
 */
export const getGridViewFilterForLoggedInUser = getPageFilterForLoggedInUser(LOCAL_STORAGE_LIVE_GRID_FILTERS);

/**
 * Store a value for one of live grid view's filters.
 * @param {number} loggedInUserUid Uid of logged in user.
 * @param {('status'|'tags')} filter Id of filter.
 * @param {*} value Value to store.
 */
export const setGridViewFilters = setPageFilterForLoggedInUser(LOCAL_STORAGE_LIVE_GRID_FILTERS);

/**
 * Remove all live grid view filters for the logged in user from local storage.
 * @param {number} loggedInUserUid Uid of logged in user.
 */
export const clearGridViewFilters = clearPageFiltersForLoggedInUser(LOCAL_STORAGE_LIVE_GRID_FILTERS);

/**
 * Get stored value for a filter on a selected user's alerts page.
 * @param {number} loggedInUserUid Uid of logged in user.
 * @param {string} page Alerts page identifier.
 * @param {string} filter Filter key.
 * @returns {*} Filter value.
 */
export const getAlertsFilterForPageForLoggedInUser =
    getPageScopedFilterForLoggedInUser(LOCAL_STORAGE_ALERTS_FILTERS);

/**
 * Store a value for a filter on a selected user's alerts page.
 * @param {number} loggedInUserUid Uid of logged in user.
 * @param {string} page Alerts page identifier.
 * @param {string} filter Filter key.
 * @param {*} value Value to store.
 */
export const setAlertsFilterForPageForLoggedInUser =
    setPageScopedFilterForLoggedInUser(LOCAL_STORAGE_ALERTS_FILTERS);

/**
 * Clear all stored filters for a selected user's alerts page.
 * @param {number} loggedInUserUid Uid of logged in user.
 * @param {string} page Alerts page identifier.
 */
export const clearAlertsFiltersForPageForLoggedInUser =
    clearPageScopedFiltersForLoggedInUser(LOCAL_STORAGE_ALERTS_FILTERS);

/**
 * Get whether live camera navigator was expanded on user's last visit.
 * @returns {boolean} Was navigator expanded?
 */
export const getCameraNavigatorExpanded = () => {
    if (localStorage[LOCAL_STORAGE_CAMERA_NAVIGATOR_EXPANDED]) {
        return localStorage[LOCAL_STORAGE_CAMERA_NAVIGATOR_EXPANDED] === 'true';
    }
    return undefined;
}

/**
 * Update in local storage whether user has expanded the live camera navigator.
 * @param {boolean} expanded Is navigator expanded?
 */
export const setCameraNavigatorExpanded = (expanded) => {
    localStorage[LOCAL_STORAGE_CAMERA_NAVIGATOR_EXPANDED] = expanded;
};

export const getPlaybackTime = () => {
    const time = parseInt(localStorage[LOCAL_STORAGE_PLAYBACK_TIME]);
    return !isNaN(time) ? time : undefined;
}

export const setPlaybackTime = (time) => {
    localStorage[LOCAL_STORAGE_PLAYBACK_TIME] = time;
}

export const getPlaybackTimelineVisibleRangeSize = () => {
    const range = parseInt(localStorage[LOCAL_STORAGE_PLAYBACK_VISIBLE_RANGE]);
    return !isNaN(range) ? range : undefined;
}

export const setPlaybackTimelineVisibleRangeSize = (range) => {
    localStorage[LOCAL_STORAGE_PLAYBACK_VISIBLE_RANGE] = range;
}

export const setShowCameraNames = show => {
    localStorage[LOCAL_STORAGE_SHOW_CAMERA_NAMES] = show;
};

export const getShowCameraNames = () => 
    localStorage[LOCAL_STORAGE_SHOW_CAMERA_NAMES] === 'true' ? true :
    localStorage[LOCAL_STORAGE_SHOW_CAMERA_NAMES] === 'false' ? false : 
    // Default to showing on mobile app, hidden in browser
    isMobileApp
;

/**
 * Store whether live grid page shows in stills mode in local storage.
 * @param {boolean} stillsMode New value.
 */
export const setLiveStillsMode = stillsMode => {
    localStorage[LOCAL_STORAGE_LIVE_STILLS_MODE] = stillsMode;
};

/**
 * Get stored value of live grid page stills/video toggle. Returns undefined if nothing is stored.
 * @returns {boolean|undefined} Stored value.
 */
export const getLiveStillsMode = () => {
    if (localStorage[LOCAL_STORAGE_LIVE_STILLS_MODE]) {
        return localStorage[LOCAL_STORAGE_LIVE_STILLS_MODE] === 'true';
    }
}

/**
 * Get stored value for the logged in user for one of the filters on the Vehicles page.
 * @param {number} loggedInUserUid Uid of logged in user.
 * @param {string} filter Filter key.
 * @returns {*} Filter value.
 */
export const getVehiclesFilterForLoggedInUser = getPageFilterForLoggedInUser(LOCAL_STORAGE_VEHICLES_FILTERS);

/**
 * Store a value for one of the filters on the Vehicles page.
 * @param {number} loggedInUserUid Uid of logged in user.
 * @param {('cameras'|'dateRange')} filter Id of filter.
 * @param {*} value Value to store.
 */
export const setVehiclesFilter = setPageFilterForLoggedInUser(LOCAL_STORAGE_VEHICLES_FILTERS);

/**
 * Clear all stored filters for the logged in user on the Vehicles page.
 * @param {number} loggedInUserUid Uid of logged in user.
 */
export const clearVehiclesFilters = clearPageFiltersForLoggedInUser(LOCAL_STORAGE_VEHICLES_FILTERS);

/**
 * Get stored value for the logged in user for one of the filters on the People Counting page.
 * @param {number} loggedInUserUid Uid of logged in user.
 * @param {string} filter Filter key.
 * @returns {*} Filter value.
 */
export const getPeopleCountingFilterForLoggedInUser = getPageFilterForLoggedInUser(LOCAL_STORAGE_PEOPLE_COUNTING_FILTERS);

/**
 * Store a value for one of the filters on the People Counting page.
 * @param {number} loggedInUserUid Uid of logged in user.
 * @param {('cameras'|'dateRange')} filter Id of filter.
 * @param {*} value Value to store.
 */
export const setPeopleCountingFilter = setPageFilterForLoggedInUser(LOCAL_STORAGE_PEOPLE_COUNTING_FILTERS);

/**
 * Clear all stored filters for the logged in user on the People Counting page.
 * @param {number} loggedInUserUid Uid of logged in user.
 */
export const clearPeopleCountingFilters = clearPageFiltersForLoggedInUser(LOCAL_STORAGE_PEOPLE_COUNTING_FILTERS);

/**
 * Get stored value for the logged in user for one of the filters on the PPE dashboard.
 * @param {number} loggedInUserUid Uid of logged in user.
 * @param {string} filter Filter key.
 * @returns {*} Filter value.
 */
export const getPPEFilterForLoggedInUser = getPageFilterForLoggedInUser(LOCAL_STORAGE_PPE_FILTERS);

/**
 * Store a value for one of the filters on the PPE dashboard.
 * @param {number} loggedInUserUid Uid of logged in user.
 * @param {('cameras'|'dateRange')} filter Id of filter.
 * @param {*} value Value to store.
 */
export const setPPEFilter = setPageFilterForLoggedInUser(LOCAL_STORAGE_PPE_FILTERS);

/**
 * Clear all stored filters for the logged in user on the PPE dashboard.
 * @param {number} loggedInUserUid Uid of logged in user.
 */
export const clearPPEFilters = clearPageFiltersForLoggedInUser(LOCAL_STORAGE_PPE_FILTERS);

export const getSavedViewerTimezone = () => localStorage.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getHideInstallerWarningPreference = () => JSON.parse(localStorage[LOCAL_STORAGE_SHOW_INSTALLER_WARNING]);

export const setHideInstallerWarningPreference = () => {
    localStorage[LOCAL_STORAGE_SHOW_INSTALLER_WARNING] = false;
}

/**
 * Get the last selected organisation ids used by ordercam from local storage.
 * @returns {number[]|void} Returns ids if stored values were found.
 */
export const getOrdercamSelectedOrganisations = () => {
    if (localStorage[LOCAL_STORAGE_ORDERCAM_SELECTED_ORGS]) {
        try {
            const stored = JSON.parse(localStorage[LOCAL_STORAGE_ORDERCAM_SELECTED_ORGS]);
            if (Array.isArray(stored)) {
                return stored;
            }
        } catch (error) {
            console.error(error);
        }
    }
}

/**
 * Update selected ordercam organisations in local storage.
 * @param {number[]} organisationIds Ids of selected organisations.
 */
export const setOrdercamSelectedOrganisations = (organisationIds) => {
    localStorage[LOCAL_STORAGE_ORDERCAM_SELECTED_ORGS] = JSON.stringify(organisationIds);
}

/**
 * Get number of columns cameras grid was last using on mobile app.
 * @returns {number} Number of columns.
 */
export const getMobileAppCamerasGridColumns = () => {
    if (localStorage[LOCAL_STORAGE_MOBILE_APP_CAMERAS_GRID_COLUMNS]) {
        const columns = parseInt(localStorage[LOCAL_STORAGE_MOBILE_APP_CAMERAS_GRID_COLUMNS]);
        if (!isNaN(columns)) {
            return columns;
        }
    }
}

/**
 * Store number of columns used in cameras grid on mobile app.
 * @param {number} columns
 */
export const setMobileAppCamerasGridColumns = (columns) => {
    localStorage[LOCAL_STORAGE_MOBILE_APP_CAMERAS_GRID_COLUMNS] = columns;
}


/* -------------------------------------------------------------------------- */
/*                               Session storage                              */
/* -------------------------------------------------------------------------- */

const SESSION_STORAGE_LOGGED_IN_REDIRECT_KEY = 'loggedInRedirect';

export const setLoggedInRedirectInSessionStorage = location => {
    sessionStorage[SESSION_STORAGE_LOGGED_IN_REDIRECT_KEY] = location;
}

export const getLoggedInRedirectInSessionStorage = () => sessionStorage[SESSION_STORAGE_LOGGED_IN_REDIRECT_KEY];

export const clearLoggedInRedirectInSessionStorage = () => {
    sessionStorage.removeItem(SESSION_STORAGE_LOGGED_IN_REDIRECT_KEY);
}

/**
 * Get the camera order for grid view.
 * @param {number} loggedInUserUid Uid of logged in user.
 * @param {string} [filterKey] Optional filter combination key for mobile app.
 * @returns {string[]|undefined} Array of camera uidds in order.
 */
export const getGridCamerasOrder = (loggedInUserUid, filterKey='no-filter') => {
    if (localStorage[LOCAL_STORAGE_GRID_CAMERA_ORDER]) {
        try {
            const stored = JSON.parse(localStorage[LOCAL_STORAGE_GRID_CAMERA_ORDER])?.[loggedInUserUid];
            if (!_.isNil(stored)) {
                return stored[filterKey];
            }
        } catch (error) {
            console.error(error);
        }
    }
    return undefined;
};

/**
 * Store the camera order for grid view.
 * @param {number} loggedInUserUid Uid of logged in user.
 * @param {string[]} cameraOrder Array of camera uidds in order.
 * @param {string} [filterKey] Optional filter combination key for mobile app, when not used just use 'no-filter'
 */
export const setGridCamerasOrder = (loggedInUserUid, cameraOrder, filterKey='no-filter') => {
    let cameraOrders = {};
    if (localStorage[LOCAL_STORAGE_GRID_CAMERA_ORDER]) {
        try {
            cameraOrders = JSON.parse(localStorage[LOCAL_STORAGE_GRID_CAMERA_ORDER]);
        } catch (error) {
            console.error(`Unable to parse ${LOCAL_STORAGE_GRID_CAMERA_ORDER} in local storage so overwriting. Error: ${error}`);
        }
    }
    
    if (!cameraOrders[loggedInUserUid] || Array.isArray(cameraOrders[loggedInUserUid])) {
        cameraOrders[loggedInUserUid] = {};
    }
    cameraOrders[loggedInUserUid][filterKey] = cameraOrder;
    
    localStorage[LOCAL_STORAGE_GRID_CAMERA_ORDER] = JSON.stringify(cameraOrders);
};


export const getGridLayoutFromLocalStorage = () => {
    if (localStorage[LOCAL_STORAGE_OBJECTS_GRID_LAYOUT]) {
        try {
            return parseInt(localStorage[LOCAL_STORAGE_OBJECTS_GRID_LAYOUT]);
        } catch (error) {
        }
    }
    return undefined;
};

export const setGridLayoutInLocalStorage = (layoutKey) => {
    localStorage[LOCAL_STORAGE_OBJECTS_GRID_LAYOUT] = layoutKey;
};

/**
 * Check if a feature banner has been dismissed by a user.
 * @param {number} loggedInUserUid Uid of logged in user.
 * @param {string} featureId Identifier for the feature (e.g. 'smartMotion').
 * @returns {boolean} Whether the banner has been dismissed.
 */
export const isFeatureBannerDismissed = (loggedInUserUid, featureId) => {
    if (localStorage[LOCAL_STORAGE_FEATURE_BANNER_DISMISSED]) {
        try {
            const dismissed = JSON.parse(localStorage[LOCAL_STORAGE_FEATURE_BANNER_DISMISSED]);
            return dismissed?.[loggedInUserUid]?.[featureId] === true;
        } catch (error) {
            console.error(error);
        }
    }
    return false;
};

/**
 * Mark a feature banner as dismissed for a user.
 * @param {number} loggedInUserUid Uid of logged in user.
 * @param {string} featureId Identifier for the feature (e.g. 'smartMotion').
 */
export const setFeatureBannerDismissed = (loggedInUserUid, featureId) => {
    let dismissed = {};
    if (localStorage[LOCAL_STORAGE_FEATURE_BANNER_DISMISSED]) {
        try {
            dismissed = JSON.parse(localStorage[LOCAL_STORAGE_FEATURE_BANNER_DISMISSED]);
        } catch (error) {
            console.error(`Unable to parse ${LOCAL_STORAGE_FEATURE_BANNER_DISMISSED} in local storage so overwriting. Error: ${error}`);
        }
    }
    if (!dismissed[loggedInUserUid]) {
        dismissed[loggedInUserUid] = {};
    }
    dismissed[loggedInUserUid][featureId] = true;
    localStorage[LOCAL_STORAGE_FEATURE_BANNER_DISMISSED] = JSON.stringify(dismissed);
};
